// Pass 2.13 — search results dropdown.
//
// Anchored to the SearchBar; ~480px wide × 60vh tall, scrolls
// internally. Each row is a single search hit with workspace +
// conversation context, sender, timestamp, and a 140-char snippet
// centred on the match (matched substring wrapped in <mark>).
// Clicking a row invokes onPickHit but does NOT close the
// dropdown — staying open lets the user keep clicking through
// hits without re-typing.

import { useEffect, useMemo, useRef, type ReactNode } from "react";
import { Hash, Lock, User, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import type { ApiError, SearchHit, SearchScope } from "../lib/api";

const SNIPPET_RADIUS = 70; // chars on either side of the match

const TYPE_ICON: Record<string, LucideIcon> = {
  public_channel: Hash,
  private_channel: Lock,
  mpim: Users,
  im: User,
};

interface SearchResultsProps {
  query: string;
  scope: SearchScope;
  hits: SearchHit[];
  totalCount: number;
  loading: boolean;
  error: ApiError | null;
  hasMore: boolean;
  /** Whether the dropdown is currently visible. The parent toggles
   *  this on focus / outside click / Esc. */
  open: boolean;
  /** Render workspace name on each row (cross-workspace search). */
  showWorkspaceColumn: boolean;
  /** Pivot to "all" scope from inside the empty state. Available
   *  only when current scope is 'workspace' or 'conversation'. */
  onPivotToAll: () => void;
  onPickHit: (hit: SearchHit) => void;
  onLoadMore: () => void;
  onClose: () => void;
}

export function SearchResults({
  query,
  scope,
  hits,
  totalCount,
  loading,
  error,
  hasMore,
  open,
  showWorkspaceColumn,
  onPivotToAll,
  onPickHit,
  onLoadMore,
  onClose,
}: SearchResultsProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Click-outside close — but exclude clicks on the SearchBar
  // itself (otherwise typing in the bar would close the dropdown
  // as soon as the click resolved).
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent): void {
      const wrap = wrapRef.current;
      const target = e.target as Node;
      if (wrap && wrap.contains(target)) return;
      // Don't close when the click landed inside the search bar.
      const bar = document.querySelector('[data-testid="search-bar"]');
      if (bar && bar.contains(target)) return;
      onClose();
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, onClose]);

  if (!open) return null;
  const trimmed = query.trim();
  if (!trimmed) return null;

  return (
    <div
      ref={wrapRef}
      data-testid="search-results"
      className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-40 w-[480px] max-w-[95vw] max-h-[60vh] overflow-y-auto bg-theme-bg-card border border-theme-border-default shadow-lg scrollbar-thin"
    >
      <div
        data-testid="search-results-header"
        className="px-4 py-2 border-b border-theme-border-default flex items-center justify-between flex-shrink-0 bg-theme-bg-card sticky top-0 z-10"
      >
        <span className="font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary">
          {loading && hits.length === 0
            ? "Searching…"
            : `${totalCount} result${totalCount === 1 ? "" : "s"}`}
        </span>
      </div>

      {error && (
        <div
          data-testid="search-results-error"
          className="p-4 font-mono text-xs text-theme-error-text"
        >
          Search failed: {error.message}
        </div>
      )}

      {!error && loading && hits.length === 0 && (
        <SkeletonRow />
      )}

      {!error && !loading && hits.length === 0 && (
        <EmptyState
          query={trimmed}
          scope={scope}
          onPivotToAll={onPivotToAll}
        />
      )}

      {hits.map((h) => (
        <ResultRow
          key={`${h.team_id}|${h.conversation_id}|${h.parent_ts ?? ""}|${h.ts}`}
          hit={h}
          query={trimmed}
          showWorkspaceColumn={showWorkspaceColumn}
          onPick={onPickHit}
        />
      ))}

      {hasMore && (
        <div className="px-4 py-3 flex justify-center border-t border-theme-border-default">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={loading}
            data-testid="search-results-load-more"
            className="px-3 py-1.5 border border-theme-border-default font-mono text-[10px] uppercase tracking-widest text-theme-text-secondary hover:text-theme-text-primary hover:border-theme-border-emphasis transition-colors duration-150 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Load 30 more"}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- result row -----------------------------------------------------

interface ResultRowProps {
  hit: SearchHit;
  query: string;
  showWorkspaceColumn: boolean;
  onPick: (hit: SearchHit) => void;
}

function ResultRow({ hit, query, showWorkspaceColumn, onPick }: ResultRowProps) {
  const Icon = TYPE_ICON[hit.conversation_type] ?? Hash;
  const date = useMemo(() => formatHitDate(hit.ts), [hit.ts]);
  const snippet = useMemo(() => buildSnippet(hit.text, query), [hit.text, query]);

  return (
    <button
      type="button"
      onClick={() => onPick(hit)}
      data-testid="search-result-row"
      data-hit-team-id={hit.team_id}
      data-hit-conv-id={hit.conversation_id}
      data-hit-ts={hit.ts}
      data-hit-parent-ts={hit.parent_ts ?? ""}
      data-hit-deleted={hit.deleted ? "true" : "false"}
      className="w-full text-left px-4 py-3 border-b border-theme-border-default hover:bg-theme-bg-conv-hover transition-colors duration-150 block"
    >
      <div className="flex items-center gap-2 mb-1 font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary">
        {showWorkspaceColumn && (
          <>
            <span data-testid="search-result-workspace">{hit.team_name}</span>
            <span>·</span>
          </>
        )}
        <Icon className="w-3 h-3" strokeWidth={1.5} aria-hidden />
        <span className="truncate" title={hit.conversation_label}>
          {hit.conversation_label}
        </span>
        {hit.parent_ts !== null && <span className="opacity-60">· thread reply</span>}
      </div>
      <div className="flex items-center gap-2 mb-1 font-mono text-[11px]">
        <span className="text-theme-text-secondary truncate">
          {hit.user_display_name}
        </span>
        <span className="text-theme-text-tertiary">·</span>
        <span className="text-theme-text-tertiary">{date}</span>
        {hit.deleted && (
          <span
            data-testid="search-result-deleted-badge"
            className="text-theme-error-text"
          >
            (deleted)
          </span>
        )}
      </div>
      <div
        data-testid="search-result-snippet"
        className={[
          "font-sans text-[12px] text-theme-text-primary leading-snug",
          hit.deleted ? "line-through opacity-60" : "",
        ].join(" ")}
      >
        {snippet}
      </div>
    </button>
  );
}

// ---------- helpers --------------------------------------------------------

function formatHitDate(ts: string): string {
  const d = new Date(Number.parseFloat(ts) * 1000);
  if (Number.isNaN(d.getTime())) return ts;
  const now = new Date();
  const sameYear = d.getFullYear() === now.getFullYear();
  const month = d.toLocaleString(undefined, { month: "short" });
  const day = d.getDate();
  return sameYear ? `${month} ${day}` : `${month} ${day}, ${d.getFullYear()}`;
}

/**
 * Build a ~140-char snippet centred on the first case-insensitive
 * match of `query` in `text`. Matched substring is wrapped in
 * <mark>. If query isn't actually found (shouldn't happen — the
 * service vouches for it — but tolerate), return the leading
 * 140 chars unmarked.
 */
function buildSnippet(text: string, query: string): ReactNode[] {
  const haystack = text;
  const idx = haystack.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) {
    const sliced = haystack.slice(0, SNIPPET_RADIUS * 2);
    return [
      <span key="plain">
        {sliced}
        {haystack.length > sliced.length ? "…" : ""}
      </span>,
    ];
  }
  const start = Math.max(0, idx - SNIPPET_RADIUS);
  const end = Math.min(haystack.length, idx + query.length + SNIPPET_RADIUS);
  const before = haystack.slice(start, idx);
  const matched = haystack.slice(idx, idx + query.length);
  const after = haystack.slice(idx + query.length, end);
  const out: ReactNode[] = [];
  if (start > 0) out.push(<span key="ellipsis-start">…</span>);
  out.push(<span key="before">{before}</span>);
  out.push(
    <mark
      key="match"
      data-testid="search-result-snippet-match"
      className="bg-theme-accent-bg text-theme-accent-text px-0.5"
    >
      {matched}
    </mark>,
  );
  out.push(<span key="after">{after}</span>);
  if (end < haystack.length) out.push(<span key="ellipsis-end">…</span>);
  return out;
}

// ---------- placeholder + empty + skeleton --------------------------------

function SkeletonRow() {
  return (
    <div className="p-4">
      <div className="h-3 w-48 bg-theme-border-default mb-2 animate-pulse" />
      <div className="h-3 w-32 bg-theme-border-default mb-2 animate-pulse" />
      <div className="h-3 w-full bg-theme-border-default animate-pulse" />
    </div>
  );
}

interface EmptyStateProps {
  query: string;
  scope: SearchScope;
  onPivotToAll: () => void;
}

function EmptyState({ query, scope, onPivotToAll }: EmptyStateProps) {
  const scopeLabel =
    scope === "conversation"
      ? "this conversation"
      : scope === "workspace"
        ? "this workspace"
        : "any workspace";
  return (
    <div
      data-testid="search-results-empty"
      className="p-6 flex flex-col items-center gap-3"
    >
      <p className="font-mono text-[11px] text-theme-text-tertiary text-center">
        No matches for{" "}
        <span className="text-theme-text-secondary">"{query}"</span> in{" "}
        {scopeLabel}.
      </p>
      {scope !== "all" && (
        <button
          type="button"
          onClick={onPivotToAll}
          data-testid="search-results-pivot-all"
          className="px-3 py-1.5 border border-theme-border-default font-mono text-[10px] uppercase tracking-widest text-theme-text-secondary hover:text-theme-text-primary hover:border-theme-border-emphasis transition-colors duration-150"
        >
          Search all workspaces
        </button>
      )}
    </div>
  );
}
